import type { ContentImportCollection, ContentImportItemDto } from "../types";
import { validateImportItem } from "../validateImportItem";
import {
  fail,
  mergeResults,
  ok,
  rejectInternalId,
  requireLocalizedString,
  requireNonEmptyString,
  type ValidationResult,
} from "../validationHelpers";
import type {
  FlattenedLearningPath,
  LearningPathGraphIssue,
  LearningPathSpaceDto,
} from "./types";

const FORBIDDEN_FIELDS = ["createdAt", "updatedAt", "accessCode"] as const;

const QUESTION_MODALITIES = new Set([
  "LEVEL_MODALITY_QUIZ",
  "LEVEL_MODALITY_TREASURE_HUNT",
  "LEVEL_MODALITY_TOWER_CLIMB",
]);

function rejectForbiddenFields(item: Record<string, unknown>): ValidationResult {
  for (const field of FORBIDDEN_FIELDS) {
    if (field in item) {
      return fail(`"${field}" is not allowed in import JSON`);
    }
  }
  return ok;
}

function validateSpace(space: LearningPathSpaceDto): ValidationResult {
  const record = space as unknown as Record<string, unknown>;
  return mergeResults(
    rejectInternalId(record),
    rejectForbiddenFields(record),
    requireNonEmptyString(space.name, "space.name"),
    requireLocalizedString(space.title, "space.title"),
    requireLocalizedString(space.body, "space.body")
  );
}

function codeOf(item: ContentImportItemDto): string | undefined {
  return typeof item.code === "string" && item.code.trim().length > 0
    ? item.code
    : undefined;
}

function collectDuplicateCodes(
  items: ContentImportItemDto[],
  kind: string,
  issues: LearningPathGraphIssue[]
): Set<string> {
  const seen = new Map<string, number>();
  const codes = new Set<string>();
  for (let index = 0; index < items.length; index++) {
    const item = items[index];
    if (!item) {
      continue;
    }
    const code = codeOf(item);
    if (!code) {
      continue;
    }
    const previous = seen.get(code);
    if (previous !== undefined) {
      issues.push({
        path: `${kind}[${index}].code`,
        reason: `duplicate ${kind} code "${code}" (also at ${kind}[${previous}])`,
      });
    } else {
      seen.set(code, index);
    }
    codes.add(code);
  }
  return codes;
}

function validateItems(
  collection: ContentImportCollection,
  items: ContentImportItemDto[],
  issues: LearningPathGraphIssue[]
): void {
  for (let index = 0; index < items.length; index++) {
    const item = items[index];
    if (!item) {
      continue;
    }
    const forbidden = rejectForbiddenFields(item);
    if (!forbidden.ok) {
      issues.push({ path: `${collection}[${index}]`, reason: forbidden.reason });
    }
    const result = validateImportItem(collection, item);
    if (!result.ok) {
      issues.push({
        path: `${collection}[${index}]`,
        reason: result.reason,
      });
    }
  }
}

function questionDifficultiesOf(level: ContentImportItemDto): string[] {
  if (!Array.isArray(level.questionDifficulties)) {
    return [];
  }
  return level.questionDifficulties.filter(
    (value): value is string => typeof value === "string" && value.length > 0
  );
}

export function validateLearningPathGraph(
  flattened: FlattenedLearningPath
): LearningPathGraphIssue[] {
  const issues: LearningPathGraphIssue[] = [];

  const spaceResult = validateSpace(flattened.space);
  if (!spaceResult.ok) {
    issues.push({ path: "space", reason: spaceResult.reason });
  }

  if (flattened.units.length === 0) {
    issues.push({ path: "units", reason: "at least one unit is required" });
  }

  validateItems("units", flattened.units, issues);
  validateItems("stories", flattened.stories, issues);
  validateItems("questions", flattened.questions, issues);
  validateItems("levels", flattened.levels, issues);

  const unitCodes = collectDuplicateCodes(flattened.units, "units", issues);
  const storyCodes = collectDuplicateCodes(flattened.stories, "stories", issues);
  collectDuplicateCodes(flattened.questions, "questions", issues);
  collectDuplicateCodes(flattened.levels, "levels", issues);

  const questionsByUnit = new Map<string, number>();
  const difficultiesByUnit = new Map<string, Set<string>>();
  for (const question of flattened.questions) {
    const unitCode =
      typeof question.unitCode === "string" ? question.unitCode : "";
    questionsByUnit.set(unitCode, (questionsByUnit.get(unitCode) ?? 0) + 1);
    const difficulty =
      typeof question.difficulty === "string" ? question.difficulty : "";
    if (!difficulty) {
      continue;
    }
    let diffs = difficultiesByUnit.get(unitCode);
    if (!diffs) {
      diffs = new Set();
      difficultiesByUnit.set(unitCode, diffs);
    }
    diffs.add(difficulty);
  }

  const levelsByUnit = new Map<string, number>();
  const ordersByUnit = new Map<string, Map<number, number>>();

  for (let index = 0; index < flattened.levels.length; index++) {
    const level = flattened.levels[index];
    if (!level) {
      continue;
    }
    const unitCode =
      typeof level.unitCode === "string" ? level.unitCode : "";
    levelsByUnit.set(unitCode, (levelsByUnit.get(unitCode) ?? 0) + 1);

    if (typeof level.order === "number") {
      let orders = ordersByUnit.get(unitCode);
      if (!orders) {
        orders = new Map();
        ordersByUnit.set(unitCode, orders);
      }
      const previous = orders.get(level.order);
      if (previous !== undefined) {
        issues.push({
          path: `levels[${index}].order`,
          reason: `duplicate order ${level.order} in unit "${unitCode}" (also at levels[${previous}])`,
        });
      } else {
        orders.set(level.order, index);
      }
    }

    if (level.modality === "LEVEL_MODALITY_STORY") {
      const storyCode =
        typeof level.storyCode === "string" ? level.storyCode : "";
      if (storyCode && !storyCodes.has(storyCode)) {
        issues.push({
          path: `levels[${index}].storyCode`,
          reason: `story "${storyCode}" was not found in this file`,
        });
      }
    }

    if (
      typeof level.modality === "string" &&
      QUESTION_MODALITIES.has(level.modality)
    ) {
      const filters = questionDifficultiesOf(level);
      if (filters.length > 0) {
        const available = difficultiesByUnit.get(unitCode) ?? new Set();
        const hasMatch = filters.some((difficulty) => available.has(difficulty));
        if (!hasMatch) {
          issues.push({
            path: `levels[${index}].questionDifficulties`,
            reason: `unit "${unitCode}" has no questions matching questionDifficulties`,
          });
        }
      } else if ((questionsByUnit.get(unitCode) ?? 0) === 0) {
        issues.push({
          path: `levels[${index}]`,
          reason: `unit "${unitCode}" has no questions for ${level.modality}`,
        });
      }
    }
  }

  for (let index = 0; index < flattened.units.length; index++) {
    const unit = flattened.units[index];
    if (!unit) {
      continue;
    }
    const unitCode = codeOf(unit);
    if (!unitCode) {
      continue;
    }
    if ((levelsByUnit.get(unitCode) ?? 0) === 0) {
      issues.push({
        path: `units[${index}]`,
        reason: `unit "${unitCode}" must have at least one level`,
      });
    }
  }

  for (let index = 0; index < flattened.stories.length; index++) {
    const story = flattened.stories[index];
    if (!story) {
      continue;
    }
    const unitCode =
      typeof story.unitCode === "string" ? story.unitCode : "";
    if (unitCode && !unitCodes.has(unitCode)) {
      issues.push({
        path: `stories[${index}].unitCode`,
        reason: `unit "${unitCode}" was not found in this file`,
      });
    }
  }

  for (let index = 0; index < flattened.questions.length; index++) {
    const question = flattened.questions[index];
    if (!question) {
      continue;
    }
    const unitCode =
      typeof question.unitCode === "string" ? question.unitCode : "";
    if (unitCode && !unitCodes.has(unitCode)) {
      issues.push({
        path: `questions[${index}].unitCode`,
        reason: `unit "${unitCode}" was not found in this file`,
      });
    }
  }

  for (let index = 0; index < flattened.levels.length; index++) {
    const level = flattened.levels[index];
    if (!level) {
      continue;
    }
    const unitCode =
      typeof level.unitCode === "string" ? level.unitCode : "";
    if (unitCode && !unitCodes.has(unitCode)) {
      issues.push({
        path: `levels[${index}].unitCode`,
        reason: `unit "${unitCode}" was not found in this file`,
      });
    }
  }

  return issues;
}
